/*
  Class strings shared by more than one primitive.

  They live in a module rather than being repeated inline so that the rules §6.2 states
  as prose ("one density", "a visible keyboard focus ring") are greppable: change the
  constant and every table and control moves together.

  Tailwind scans src/**\/*.{ts,tsx}, so a literal class string in a .ts file is picked up
  by the JIT exactly as it would be in JSX. A string *built* at runtime would not be.
*/

/*
  The focus ring, on every interactive thing this feature adds.

  focus-visible rather than focus so a mouse click does not leave a ring behind, and the
  gold is goldText (D20) — text-pcg-gold is 1.69:1 on paper and a ring in it would be
  invisible in the light theme.
*/
/*
  Image loading hints, as attribute spreads. The raw uploads these tiles show are 10-300 KB
  each with no thumbnails, so WHEN they load matters:

    - LAZY_IMG for tiles and grids: the browser skips an image until it is near the
      viewport, and decodes it off the main thread. A card-view grid of 1,000 sets
      otherwise asks for all 1,000 logos at once.
    - HERO_IMG for the one header image that is the page's largest paint: it goes to the
      front of the queue instead of sharing bandwidth with every tile below it.

  `fetchpriority` is spread rather than written as a prop because neither React 17 nor its
  types know it; React passes an unknown lowercase attribute through to the DOM as is.
*/
export const LAZY_IMG = { loading: "lazy", decoding: "async" } as const;
export const HERO_IMG: Record<string, string> = { fetchpriority: "high" };

export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pcg-goldText focus-visible:ring-offset-2 focus-visible:ring-offset-pcg-card";

/* The same ring where an offset would be clipped — inside a row or a scroll container. */
export const FOCUS_RING_INSET =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pcg-goldText";

/*
  The chrome every form control in this feature shares.

  `skin-control` is the gilded skin's hook and does nothing in navy. It takes the radius
  from 6px to 4px and no more: controls keep a radius in gilded because a square input
  beside a pill button reads as unfinished, and the pill belongs to the primary action
  alone.

  The border is `pcg-controlBorder`, not the `pcg-border` every other edge on the site uses.
  That is the WCAG 1.4.11 fix and it is deliberately scoped to this constant: a control here
  is `appearance-none` or empty, its fill sits 1.05:1 dark / 1.13:1 light from the page, and
  so the boundary is the sole identifier and owes 3:1. Bands, tiles, table rules and the tile
  dividers are decorative separators 1.4.11 does not reach, and they keep `pcg-border`
  untouched. Do not "simplify" this back to the shared token, and do not spread
  `controlBorder` onto text-labelled buttons — their label identifies them already.
*/
export const CONTROL_BASE =
  "skin-control rounded-md border border-pcg-controlBorder bg-pcg-card text-sm text-pcg-text disabled:cursor-not-allowed disabled:opacity-60";

/* The label above a control. */
export const CONTROL_LABEL =
  "text-xs font-semibold uppercase tracking-wide text-pcg-textMuted";

/*
  An in-content link (D25).

  index.css's global `a { color: black; text-decoration-color: #cf974f }` is gone, so a
  rebuilt link that declares nothing inherits the body colour and stops looking like a
  link at all. Gold is goldText, never gold — brand gold as text on paper is 1.69:1.

  The focus ring is the INSET one: these links live inside table cells and the table's own
  overflow-auto scroll container, where a ring-offset is clipped away.
*/
export const LINK =
  `rounded-sm font-medium text-pcg-goldText underline decoration-pcg-goldMuted underline-offset-2 hover:decoration-pcg-goldText ${FOCUS_RING_INSET}`;

/*
  The catalogue tile, ported from admin's catalog grid
  (card-grading-admin-ui/src/pages/catalog/[gameCode]/index.tsx:369-435).

  Three things carry that layout, and each is here for a reason rather than for
  resemblance:

  - **The logo sits on white.** Every publisher logo in this catalogue is drawn for a
    white background, and several are black line art — One Piece and Topps Chrome are
    nearly invisible painted straight onto a #0B0E14 card. Admin does not have that
    problem because it never puts a logo on the dark ground.
  - **The title is page text, not link gold.** Making every tile title gold-and-underlined
    turned a grid into a wall of gold. Gold moves to hover and focus. This is a deliberate
    departure from D25's LINK, which stays exactly as it is for links inside prose and
    table cells: there a link sits among body text of the same colour and needs to be
    distinguishable at rest. A tile title is the only text of its size in its own bounded
    object, is bold where the line under it is not, and gains an underline the moment it
    is hovered or focused — so it is not colour alone doing the work.
  - **Facts sit in a row, not a stack.** The old tile centred the name, then the count,
    then the language, then the release date, each on its own line, which is what made a
    grid of them read as loose. Language and year become chips over the logo, the way
    admin does it, and the count gets the bottom row to itself.
*/
export const TILE =
  "skin-tile group flex h-full flex-col overflow-hidden rounded-lg border border-pcg-border bg-pcg-card transition-colors hover:border-pcg-goldMuted";

/*
  White in both themes: it is a backdrop for artwork, not a themed surface.

  `bg-pcg-onAccent`, NOT `bg-white`. tailwind.config.js points Tailwind's `white` at
  --pcg-fg so that text-white and friends flip with the theme, which means `bg-white`
  renders #0D1015 — near-black — the moment the site goes light. The config warns about
  exactly this trap for the three fixed surfaces of §3.4; a logo panel is a fourth. This
  was caught by measuring the computed background in light mode, not by looking at it.
*/
export const TILE_LOGO_PANEL =
  "relative block border-b border-pcg-border bg-pcg-onAccent px-4 pb-2 pt-4";

export const TILE_LOGO_IMG = "mx-auto h-[90px] w-full object-contain";

/*
  The gold pill admin uses in both corners of a tile — and the third consumer of
  `goldChrome`, for the same reason the Footer is.

  It sits on the white logo panel, and that panel is white in BOTH themes because it is a
  backdrop for artwork rather than a themed surface. A chip that themed while its
  background did not would drift between the two exactly as the Footer did (§12.15): the
  themed `--pcg-gold` is #D8A24A in dark and #DEBB68 in light, so the same chip on the
  same white would change colour with the rest of the page around it. `goldChrome` is
  brand #DEBB68 in both.

  Black rather than `text-pcg-page` for the same reason: page colour is #0B0E14 in dark
  and #F4F5F7 in light, so a themed text colour would put near-white type on a gold pill
  the moment the site went light — about 1.5:1. Black on brand gold is 11.41:1.
*/
/*
  The graded count at the foot of a game or set tile, made the tile's headline figure
  (Robert, 2026-10-19: "highlight the graded cards a little bit better"): the number in
  bold gold at stat size, the word as a small uppercase label after it — the same pairing
  as the stats band. goldText is the readable gold in every skin and mode.
*/
export const TILE_COUNT = "mt-auto flex items-baseline gap-1.5 tabular-nums";
export const TILE_COUNT_FIGURE = "text-lg font-bold leading-none text-pcg-goldText";
export const TILE_COUNT_LABEL =
  "text-[11px] font-semibold uppercase tracking-wide text-pcg-textMuted";

export const TILE_CHIP =
  "rounded-full bg-pcg-goldChrome px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-black whitespace-nowrap";

/*
  No `skin-*` marker here on purpose. This string is shared by the tile titles AND by the
  set-name links in the A-Z list, and gilded only moves the tile ones to the display
  serif — a serif in a dense list of 960 set names is the failure mode the whole look has
  to avoid. The tiles carry `skin-tile-title` at their own call sites instead.
*/
export const CARD_TITLE_LINK =
  `rounded-sm font-bold text-pcg-text hover:text-pcg-goldText hover:underline hover:decoration-pcg-goldText hover:underline-offset-2 ${FOCUS_RING_INSET} focus-visible:text-pcg-goldText focus-visible:underline`;

/*
  The page width every surface shares: AppBar, the cert lookup band, the page content and
  the Footer (Robert, 2026-10-20: every page as wide as the home page, like the events
  storefront). From `lg` it runs out to 4.5rem from each screen edge, up to 1760px of
  content; below that it is the usual 1rem gutter. Spread it onto the inner div of each
  surface, never the full-bleed outer one, so backgrounds still reach the screen edges.
*/
export const PAGE_CONTAINER = 'mx-auto w-full max-w-[calc(1760px+9rem)] px-4 lg:px-[4.5rem]';
